import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Button from '../../BaseComponents/Button';
import {
  getPmpModalitiesList,
  getPmpModalityErrorMessage,
  PmpModality,
  updatePmpModality,
} from '../../../api/pmpModalities';
import PmpModalitiesTable from './PmpModalitiesTable';
import PmpModalityModal from './PmpModalityModal';

const PmpModalitiesTab = () => {
  const [modalities, setModalities] = useState<PmpModality[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [modalityToEdit, setModalityToEdit] = useState<PmpModality | null>(null);

  const refreshModalities = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getPmpModalitiesList(undefined, undefined, 100, 0);
      setModalities(response.modalities ?? []);
    } catch (error: any) {
      console.error('Error fetching PMP modalities:', error);
      toast.error(getPmpModalityErrorMessage(error, 'Failed to load PMP modalities'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshModalities();
  }, [refreshModalities]);

  const handleAdd = () => {
    setModalityToEdit(null);
    setIsFormModalOpen(true);
  };

  const handleEdit = (modality: PmpModality) => {
    setModalityToEdit(modality);
    setIsFormModalOpen(true);
  };

  const handleToggleStatus = async (modality: PmpModality) => {
    if (!modality.id) return;

    const isCurrentlyActive = modality.is_active !== false;

    try {
      await updatePmpModality({
        id: modality.id,
        is_active: !isCurrentlyActive,
      });
      toast.success(
        isCurrentlyActive
          ? 'Modality deactivated successfully'
          : 'Modality activated successfully'
      );
      await refreshModalities();
    } catch (error: any) {
      console.error('Error updating modality status:', error);
      toast.error(getPmpModalityErrorMessage(error, 'Failed to update modality status'));
    }
  };

  const handleSaved = async () => {
    setIsFormModalOpen(false);
    setModalityToEdit(null);
    await refreshModalities();
  };

  const modalityCount = modalities.length;

  return (
    <div className='pmp-modalities-tab'>
      <div className='search-row-container'>
        <div className='search-and-filter-container'>
          <div className='count-container'>
            {modalityCount} {modalityCount === 1 ? 'Modality' : 'Modalities'}
          </div>
        </div>
        <Button isSecondary={true} text='Add Modality' onClick={handleAdd} />
      </div>
      <PmpModalitiesTable
        modalities={modalities}
        isLoading={isLoading}
        onEdit={handleEdit}
        onToggleStatus={handleToggleStatus}
      />
      <PmpModalityModal
        isOpen={isFormModalOpen}
        modalityToEdit={modalityToEdit}
        onClose={() => {
          setIsFormModalOpen(false);
          setModalityToEdit(null);
        }}
        onSaved={handleSaved}
      />
    </div>
  );
};

export default PmpModalitiesTab;
